import React from 'react';
import { FileText, ArrowRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../contexts/UserContext';
import { DocumentItem } from '../ui/DocumentItem';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/Card';
import { Tooltip } from '../ui/Tooltip';
import { cn } from '../../utils/cn';
import { useQuery } from '@tanstack/react-query';

interface Document {
  id: string;
  name: string;
  file_url: string;
  category_id: number;
  is_important: boolean;
  visibility: 'personal' | 'community';
  created_at: string;
}

interface DocumentsSectionProps {
  className?: string;
}

export function DocumentsSection({ className }: DocumentsSectionProps) {
  const { currentResidence } = useUser();
  const residenceId = currentResidence?.residence_id;

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['residence', residenceId, 'documents'],
    enabled: Boolean(residenceId),
    queryFn: async (): Promise<Document[]> => {
      if (!residenceId) return [];
      const { data, error } = await supabase
        .from('all_files')
        .select('id, name, file_url, category_id, is_important, visibility, created_at')
        .eq('residence_id', residenceId)
        .eq('visibility', 'community')
        .eq('is_important', true)
        .order('created_at', { ascending: false });
      if (error) {
        console.error('Error fetching important documents:', error);
        return [];
      }
      return (data as Document[]) || [];
    }
  });

  if (isLoading) {
    return (
      <Card className={cn('w-full bg-condo-dark', className)}>
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-6">
            <div className="p-2 bg-condo-dark border border-condo-light rounded-lg hidden sm:inline">
              <FileText className="h-5 w-5 text-condo-light" />
            </div>
            <h2 className="text-condo-light sm:text-xl font-semibold">Viktige dokumenter</h2>
          </div>
          <div className="text-condo-light">Laster dokumenter...</div>
        </div>
      </Card>
    );
  }

  if (documents.length === 0) {
    return null;
  }

  return (
    <Card 
      className={cn(
        'w-full bg-condo-dark',
        className
      )}
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle 
            icon={FileText} 
            size="lg" 
            iconVariant="light"
            className="text-condo-light"
          >
            Viktige dokumenter
          </CardTitle>
          <Tooltip content={
            <div className="whitespace-pre-line">
              Dokumenter markert som viktige i bofellesskapets dokumentarkiv.
              {'\n\n'}
              Endres ved å klikke bokmerke-ikonet i dokumenter på bofellesskapets side.
            </div>
          }>
            <Info className="h-4 w-4 text-condo-light hover:text-condo-med transition-colors cursor-help" />
          </Tooltip>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
          {documents.map((doc) => (
            <DocumentItem 
              key={doc.id} 
              id={doc.id}
              name={doc.name}
              link={doc.file_url}
              variant="dashboardImportant"
              isImportant={true}
              underlineOnHoverOnly={true}
            />
          ))}
        </div>

        <div className="mt-4 sm:mt-2 flex justify-end">
          <Link 
            to="/bofellesskapet#documents" 
            className="flex items-center gap-1 text-xs text-condo-light hover:text-condo-med transition-colors"
          >
            <span>Andre dokumenter i {currentResidence?.residence?.type === 'condo' ? 'Sameiet' : currentResidence?.residence?.type === 'cooperative' ? 'Borettslaget' : 'Bofellesskapet'}</span>
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}